import React, { useRef } from 'react'
import { useHover } from '@shared/hooks/use-hover'
import { useKeyboardShortcuts, type KeyboardShortCutItem } from '@shared/hooks/use-keyboard-shortcuts'
import { GenericContextMenu } from 'blocks/context-menu'
import { useContextMenu } from 'blocks/context-menu/use-context-menu'
import { useCxDataTable } from '../hooks/use-data-table'
import type {
  DataTableRowData,
  DataTableInstance,
  DataTableOptions,
  Xor,
} from '../types'
import { TablePaper } from './table/table-paper'

type TableInstanceProp<TData extends DataTableRowData> = {
  table: DataTableInstance<TData>
}

type Props<TData extends DataTableRowData> = Xor<TableInstanceProp<TData>, DataTableOptions<TData>>

const isTableInstanceProp = <TData extends DataTableRowData>(
  props: Props<TData>,
): props is TableInstanceProp<TData> => (props as TableInstanceProp<TData>).table !== undefined

export const CxDataTable = <TData extends DataTableRowData>(props: Props<TData>) => {
  let table: DataTableInstance<TData>
  let getContextMenu: DataTableOptions<TData>['getContextMenu']
  let onRowClick: DataTableOptions<TData>['onRowClick']
  let idKey: DataTableOptions<TData>['idKey']

  if (isTableInstanceProp(props)) {
    const { table: tableInstance } = props
    table = tableInstance
    // Context menu props need to be passed separately when using table instance
    getContextMenu = undefined
    onRowClick = undefined
    idKey = undefined
  } else {
    // @ts-expect-error We can either props or we can pass table
    table = useCxDataTable(props)
    const { getContextMenu: contextMenuGetter, onRowClick: rowClickHandler, idKey: keyId } = props
    getContextMenu = contextMenuGetter
    onRowClick = rowClickHandler
    idKey = keyId
  }

  // Context menu setup (only if getContextMenu is provided)
  const contextOpenRowRef = useRef<TData | undefined>(undefined)
  const [hoveredElementRef, hoverParentRef, setHoveredElement] = useHover()
  const { menuItems, updateMenu } = useContextMenu<TData>()

  // Set up context menu on row model changes
  React.useEffect(() => {
    if (getContextMenu) {
      const { rows } = table.getRowModel()
      updateMenu(getContextMenu?.(contextOpenRowRef.current, rows, hoveredElementRef) || [])
    }
  }, [table.getRowModel().rows, getContextMenu])

  // Keyboard shortcuts for context menu
  useKeyboardShortcuts(menuItems as KeyboardShortCutItem[])

  // Enhanced table options with context menu support
  const enhancedTable = {
    ...table,
    options: {
      ...table.options,
      onRowClick,
      getContextMenu,
      idKey,
      onRowContextMenu: getContextMenu
        ? (row: TData, e: React.MouseEvent) => {
            setHoveredElement(e.target as HTMLElement)
            contextOpenRowRef.current = row
            const { rows } = table.getRowModel()
            updateMenu(getContextMenu?.(row, rows, hoveredElementRef) || [])
          }
        : undefined,
    },
  } as DataTableInstance<TData>

  // Render with or without context menu wrapper
  if (getContextMenu) {
    return (
      <GenericContextMenu menuItems={menuItems} className="flex flex-col min-h-0">
        <div className="flex flex-col min-h-0" ref={hoverParentRef}>
          <TablePaper table={enhancedTable} />
        </div>
      </GenericContextMenu>
    )
  }

  return <TablePaper table={table} />
}
